import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';
import { AuditTrailViewer } from '../../components/AuditTrail';
import TabNavigation from '../../components/TabNavigation';
import { usePermissions } from '../../context/PermissionContext';
import { useAlert } from '../../context/AlertContext';
import { formatErrorMessage } from '../../utils/errorFormatter';
import { departmentLabel } from '../../utils/departmentLabel';
import { getCurrencySymbol } from '../../utils/regional';

const TrainingCourseDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { showSuccess, showError, confirm } = useAlert();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training-courses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourse(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load training course');
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Mandatory': return 'bg-red-100 text-red-700';
      case 'Job-Specific': return 'bg-blue-100 text-blue-700';
      case 'Refresher': return 'bg-green-100 text-green-700';
      case 'Contractor Training': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm(
      'Delete Training Course',
      'Are you sure you want to delete this training course? This action cannot be undone.',
      { destructive: true, confirmLabel: 'Delete' }
    );
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/training-courses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Training course deleted successfully');
      navigate('/training/courses');
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to delete course'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <Alert variant="destructive">
            <AlertDescription>{error || 'Training course not found'}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/training/courses')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/training/courses')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Courses
            </Button>

            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{course.course_title}</h1>
                {course.course_code && (
                  <p className="text-gray-600 mt-1">Code: {course.course_code}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getTypeColor(course.training_type)}`}>
                  {course.training_type}
                </span>
                {hasPermission('training', 'update') && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/training/courses?edit=${id}`)}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
                {/* QA #98: Delete moved here from the dashboard card, so both
                    record actions live on the Read/View page. Same confirm +
                    DELETE flow the list page used. */}
                {hasPermission('training', 'delete') && (
                  <Button variant="outline" onClick={handleDelete}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'audit'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Audit History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' ? (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Course Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {course.description && (
                    <div className="col-span-1 md:col-span-2">
                      <label className="text-sm font-semibold text-gray-600">Description</label>
                      <p className="text-gray-900 mt-1">{course.description}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Training Type</label>
                    <p className="mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTypeColor(course.training_type)}`}>
                        {course.training_type}
                      </span>
                    </p>
                  </div>

                  {/* QA #99: department was stored on create but never shown
                      here or on the card — visible only while editing. Renders the
                      stored NAME, never re-resolved from department_id, so a
                      deleted department still displays (QA #102). */}
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Department</label>
                    <p className="text-gray-900 mt-1">{departmentLabel(course)}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Trainer/Institution</label>
                    <p className="text-gray-900 mt-1">{course.trainer_institution || '—'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Duration (hours)</label>
                    <p className="text-gray-900 mt-1">{course.duration_hours || '—'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Validity Period (months)</label>
                    <p className="text-gray-900 mt-1">{course.validity_period_months || '—'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Cost</label>
                    {/* REG-06: symbol from the course's own currency; a blank
                        currency shows the bare amount, never an assumed ₹. */}
                    <p className="text-gray-900 mt-1">
                      {course.cost
                        ? `${course.currency ? `${getCurrencySymbol(course.currency)} ` : ''}${course.cost}`
                        : '—'}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Has Quiz</label>
                    <p className="mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        course.has_quiz ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {course.has_quiz ? 'Yes' : 'No'}
                      </span>
                    </p>
                  </div>

                  {course.has_quiz && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Passing Score</label>
                      <p className="text-gray-900 mt-1">{course.passing_score ? `${course.passing_score}%` : '—'}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Evidence Required</label>
                    <p className="mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        course.evidence_required ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {course.evidence_required ? 'Yes' : 'No'}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Created</label>
                    <p className="text-gray-900 mt-1">{formatDateTime(course.created_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <AuditTrailViewer
                  entityType="training_course"
                  entityId={id}
                  title="Training Course Audit History"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingCourseDetail;
